<template>
  <div class="wrapper">
    <PageSection v-for="(section, index) in sections" :key="`${index}-section`" :section="section" />
    <h1 class="page-header">
      {{ pageTitle }}
    </h1>
    <div id="dealer-locator-widget" class="page-body-content">
      <div id="storemapper" style="width: 100%">
        <p>
          Store Locator is loading from
          <a href="https://www.storemapper.co" aria-label="Opens in new tab."
            >Storemapper store locator <ExternalLinkLine role="presentation" height="14" width="14" /></a
          >...
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { QueryTypes } from '~/server/utils/contentstack';
import { contentstackRequest } from '~/util/contentstack/api';
import { metaGen } from '~/util/metadata';
import { getFirstIndex } from '~/util/arrays';
import { useRootStore } from '~/store';
const { locale } = useI18n();
const event = useRequestEvent();

const { data: asyncData, error: asyncError } = await useAsyncData(async () => {
  const contentStackData = await contentstackRequest(QueryTypes.page, '/dealer-locator', {
    event,
    query: {
      locale: locale?.value,
    },
  });
  if (!contentStackData) throw createError({ statusCode: 404, message: 'Page not found' });
  const page = getFirstIndex(contentStackData?.data?.all_page?.items);
  if (!page) throw createError({ statusCode: 404, message: 'Page not found' });
  return {
    contentStackData,
    page,
  };
});

useAsyncErrorHandling({
  error: asyncError.value,
});

const rootStore = useRootStore();
useHead({
  ...(metaGen(rootStore?.brandSettings, {
    ...(asyncData.value?.page?.seo || {}),
  }) as any),
});

const sections = computed<{ [index: string]: any }[]>(() => {
  return asyncData.value?.page?.sections || [];
});

const pageTitle = computed<string>(() => {
  return asyncData.value?.page?.display_title || asyncData.value?.page?.title || '';
});

onMounted(() => {
  injectStoreMapper();
});

function injectStoreMapper() {
  const scriptId = 'storemapper-script-loaction';
  const oldScript = document.getElementById(scriptId);
  if (oldScript) oldScript.remove();
  const script: any = document.createElement('script');
  script.src = 'https://www.storemapper.co/js/widget.js';
  script.defer = true;
  script.type = 'text/javascript';
  script.id = scriptId;
  script.setAttribute('data-storemapper-id', 3766);
  script.setAttribute('data-storemapper-start', '2017,03,27');
  document.body.appendChild(script);
}
</script>

<style lang="scss">
// TODO - Replace ALL of the following SCSS
// This scss was ported over due to not being able to see the actual store locator
#storemapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  margin-top: 70px;
  border: 2px solid $color-neutral-cool-100;
  position: relative;

  @media (min-width: 551px) {
    margin-top: 0;
  }

  @media (min-width: 801px) {
    display: block;
    border: none;
  }

  & &-left {
    order: 1;
    width: 100%;
    height: auto;
    float: none;

    @media (min-width: 801px) {
      float: left;
      width: 250px;
      height: 500px;
      border: 2px solid $color-neutral-cool-100;
    }

    @media (min-width: 1024px) {
      width: 24.25%;
    }

    #storemapper-form {
      width: calc(100% - 4rem);
      position: absolute;
      bottom: calc(100% - 30px); //before +25
      z-index: 2;
      left: 2rem;

      @media all and (max-width: 550px) {
        input[type='text'] {
          width: 100%;
          margin-bottom: 16px;
          box-shadow: 0 2px 5px 0 rgba(39, 37, 37, 0.15);
        }

        #storemapper-go {
          width: 152px;
          margin: 0 auto;
          box-shadow: 0 2px 5px 0 rgba(39, 37, 37, 0.15);
        }
      }

      @media (min-width: 551px) {
        display: flex;
        align-items: stretch;
        justify-content: center;
        box-shadow: 0 7px 13px 0 rgba(39, 37, 37, 0.15);
      }

      @media (min-width: 801px) {
        width: 720px;
        margin-left: calc((1200px - 720px) / 2);
        left: calc((100% - 800px) / 2);
        top: -37px;
        height: 32px;
      }

      label {
        display: none;
      }

      input[type='text'] {
        display: block;
        padding: 16px 20px 14px;

        @media (min-width: 551px) {
          flex-shrink: 1;
          flex-grow: 0;
          width: calc(100% - 152px);
        }
      }

      #storemapper-go {
        display: block;

        @media (min-width: 551px) {
          flex: 0 0 152px;
        }
      }

      .storemapper-error {
        width: 100%;
        text-align: center;
        position: absolute;
        top: calc(100% + 15px);
      }

      .storemapper-form-inner {
        @media (min-width: 551px) {
          flex-grow: 1;
          display: flex;
        }
      }
    }

    #storemapper-list {
      padding: 12px;

      @media all and (max-width: 1023px) {
        &:empty {
          display: none;
        }
      }

      @media (min-width: 551px) {
        height: auto;
      }

      li {
        margin-bottom: 20px;

        h4 {
          margin-top: 0;
          margin-bottom: 0;
          font-size: 24px;
        }

        p {
          margin-bottom: 12px;
          font-size: 1rem;

          a {
            font-size: 13px;
            color: #2f566a;
          }
        }

        a {
          font-weight: 600;

          &[href^='tel'] {
            color: inherit;
            font-weight: inherit;
          }
        }

        .storemapper-phone a {
          font-size: 1rem;
        }

        .storemapper-url {
          word-break: break-all;
        }

        .storemapper-email {
          word-break: break-all;
        }

        .storemapper-url,
        .storemapper-email {
          margin-bottom: 2px;
        }

        .storemapper-distance {
          display: none;
        }
      }
    }
  }

  & &-right {
    order: 0;
    float: none;
    width: 100%;
    height: 350px;
    margin-top: 40px;

    @media (min-width: 801px) {
      float: right;
      width: calc(100% - 250px - 12px);
      height: 500px;
      box-shadow: 0 7px 13px 0 rgba(39, 37, 37, 0.1);
    }

    @media (min-width: 1024px) {
      width: 74.75%;
      margin-top: 16px;
    }

    .storemapper-popup-name {
      min-width: 300px;
      margin: 0;
      font-size: 24px;

      ~ .storemapper-popup-address {
        margin-bottom: 8px;
      }

      ~ p {
        max-width: 300px;
        font-size: 1rem;
      }
    }

    .storemapper-category-name {
      display: none;
    }

    .storemapper-popup-phone > a {
      color: inherit;
    }

    > div > .gm-style {
      overflow: hidden;
    }
  }

  > small {
    order: 2;
    padding: 4px 8px;
    background-color: rgba(255, 255, 255, 0.5);
    position: absolute;

    @media (min-width: 801px) {
      position: static;
      padding: 0;
      background-color: transparent;
    }
  }

  .storemapper-categories {
    display: none;
  }
}

// Dealer Locator - Storemapper fix / hack / temp / tmp
@media only screen and (min-width: 768px) {
  #dealer-locator-widget #storemapper #storemapper-left {
    position: initial;
  }
}
#dealer-locator-widget {
  @media only screen and (max-width: 1025px) {
    margin-top: 50px;
  }
  @media only screen and (max-width: 800px) {
    margin-top: 90px;
  }
  @media only screen and (max-width: 767px) {
    margin-top: 130px;
  }
}
</style>
